import * as React from 'react'

import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Flex, Text } from '@chakra-ui/react'

import {
  Alert,
  Loading,
  PrimaryButton,
  SideMenuLayoutBreadcrumb,
  SideMenuLayoutContentLayout,
  Tag,
} from '@enechain/ecloud-designsystem'

import ApiKeysTable from '~/pages/ApiKeys/components/ApiKeysTable'

import { useApiKeys } from './hooks/useApiKeys'

const productNames = (import.meta.env.VITE_M2M_API_KEY_PRODUCTS ?? '')
  .split(',')
  .map((name) => name.trim())
  .filter((name) => name !== '')

export const ApiKeys: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiKeys = useApiKeys()

  return (
    <SideMenuLayoutContentLayout
      header={
        <Flex alignItems="center" justifyContent="space-between" width="full">
          <SideMenuLayoutBreadcrumb
            items={[
              {
                label: t('pages.api_keys.title'),
                bold: true,
              },
            ]}
          />
        </Flex>
      }
    >
      <Flex
        flexDirection="column"
        gap="3"
        maxWidth="1280px"
        margin="auto"
        padding="2"
      >
        <Flex flexDirection="column" gap="1">
          <Text textStyle="headerMd" color="Text.highEmphasis">
            {t('pages.api_keys.title')}
          </Text>
          <Text textStyle="textMd" color="Text.midEmphasis">
            {t('pages.api_keys.description.page')}
          </Text>
        </Flex>
        {/* description prop はDS 0.8.x では maxWidth 400px の Box に包まれ途中で折り返すため、
            children で渡して全幅で表示する(DS 0.9.x へのアップグレードで解消予定) */}
        <Alert status="info">
          {t('pages.api_keys.description.managedByCustomer')}
        </Alert>
        <Alert status="warning" fontWeight="bold">
          {t('pages.api_keys.description.credentialCaution')}
        </Alert>
        <Flex alignItems="center" justifyContent="space-between">
          {productNames.length > 0 && (
            <Flex alignItems="center" flexWrap="wrap" gap="2">
              <Text
                textStyle="textMd"
                color="Text.highEmphasis"
                fontWeight="bold"
              >
                {t('pages.api_keys.label.targetProducts')}
              </Text>
              {productNames.map((productName) => (
                <Tag
                  key={productName}
                  color="Text.accentPrimary"
                  backgroundColor="Surface.accentPrimaryLightLow"
                >
                  {productName}
                </Tag>
              ))}
            </Flex>
          )}
          <PrimaryButton
            onClick={() => {
              void navigate({ to: '/api_keys/create' })
            }}
          >
            {t('pages.api_keys.action.create')}
          </PrimaryButton>
        </Flex>
        <React.Suspense fallback={<Loading />}>
          {apiKeys.length === 0 ? (
            <Flex alignItems="center" justifyContent="center" height="200px">
              <Text textStyle="textMd" color="Text.midEmphasis">
                {t('pages.api_keys.description.noApiKeys')}
              </Text>
            </Flex>
          ) : (
            <ApiKeysTable />
          )}
        </React.Suspense>
      </Flex>
    </SideMenuLayoutContentLayout>
  )
}
